import { Injectable, inject } from '@angular/core';
import { IActivitiesObjectState, IActivitiesState, SignalRListener } from 'src/app/models/activity-logs/signalR.model';
import { ApiDataService } from '../http-data/apidata.service';
import { ActivityLog, DataAnalysisActivity, MyActivities } from 'src/app/models/activity-logs/activity-logs.model';
import { environment } from 'src/environments/environment';
import { addOrUpdateActivity, loadArchievedActivity, resetArchivedActivity } from 'src/app/redux/actions/activity.actions';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { IActionResult } from '../http-data/apidata.model';
import { WorkspaceService } from '../workspace/workspace.service';
import { activities } from 'src/app/redux/selectors/activity.selector';
import { AppState } from 'src/app/redux/app.state';
import { DashboardId } from 'src/app/modules/automate365-dashboard/enums/template.enum';
import { IntranetDataService } from 'src/app/modules/intranet-dashboard/services/intranet-data.service';
import { upsertTemplateInstallationRequestActivity } from 'src/app/redux/actions/templates/template-installation-requests.actions';

@Injectable({
	providedIn: 'root'
})

export class ActivityService {
	private apiData = inject(ApiDataService);
	private store = inject<Store<AppState>>(Store);
	private workspaceService = inject(WorkspaceService);
	private intranetDashboard = inject(IntranetDataService);


	/** Get all activity */
	getAllActivities(activityState: IActivitiesObjectState) {
		const filteredScans = activityState.TenantAnalyzer.filter(x =>  !x.isSchedulerScan);
		return activityState.Apply.concat(activityState.Install).concat(activityState.Download).concat(activityState.Generate).concat(activityState.Update).concat(filteredScans).concat(activityState.Pulse365Action);
	}

	/** Get current activity */
	getCurrentActivities() {
		this.apiData.get<MyActivities[]>(`${environment.urls.hubApi}api/activity/current`).subscribe(resp => {
			if (resp.data) {
				resp.data.forEach(activity => {
					this.store.dispatch(addOrUpdateActivity({ listener: activity.context, activity: activity.tasks }));
				});
			}
		});
	}

	/** Get current activity for data analysis */
	getCurrentDataAnalisys() {
		this.apiData.get<DataAnalysisActivity>(`${environment.urls.hubApi}api/activity/dataanalysis`).subscribe(resp => {
			if (resp.data) {
				if (resp.data) {
					if (resp.data.dashboards.find(d => d === DashboardId.ProductsUsage)) {
						this.intranetDashboard.updateReportProgress(resp.data.status);
					}
				}
			}
		});
	}

	/** Get archived activities */
	public getArchivedActivities(): Observable<IActionResult<MyActivities[]>> {
		return this.apiData.get<MyActivities[]>(`${environment.urls.hubApi}api/activity/archived`)
	}

	/** Get specific activity */
	getActivity(activityId: string, workspaceId?: string, fromInstallationRequests = false): Promise<MyActivities | null> {
		if (!workspaceId) {
			workspaceId = this.workspaceService.getCurrentUserWorkspaceId();
		}

		return new Promise<MyActivities | null>((resolve) => {
			this.apiData.get<MyActivities>(`${environment.urls.hubApi}api/activity/${activityId}?contextId=${workspaceId}`).subscribe({
				next: async (resp) => {
					if (resp.data) {
						if (!fromInstallationRequests) {
							this.store.dispatch(addOrUpdateActivity({ listener: resp.data.context, activity: resp.data.tasks }));
						}
						else {
							this.store.dispatch(upsertTemplateInstallationRequestActivity({ activity: resp.data.tasks }))
						}
					}
					resolve(resp.data)
				},
				error: () => {
					resolve(null)
				}
			})
		})
	}

	// Get the template installatio requests in the Store or trigger the loading process if they are not loaded
	public getArchivedActivity(): Promise<IActivitiesState> {
		return new Promise<IActivitiesState>((resolve, reject) => {
			let resolved = false
			let ranOnce = false
			const storeSubscription = this.redux_getActivitiesSelector().subscribe({
				next: (activitiesState) => {
					if (!activitiesState) {
						reject(new Error('Activity state is unavailable.'));
						resolved = true;
						if (ranOnce) storeSubscription.unsubscribe();
					} else {
						if (!activitiesState.value.archivedActivityLoading) {
							if (activitiesState.value.archivedActivityError) {
								reject(new Error('Archived activities could not be loaded.', { cause: activitiesState.value.archivedActivityError }));

								resolved = true;
								// If has already ran once then unsbcribe
								if (ranOnce) {
									storeSubscription.unsubscribe();
								}
							} else if (!activitiesState.value.archivedActivityLoaded) {
								this.redux_loadArchievedActivity()
							} else {
								resolve(activitiesState.value);

								resolved = true;
								// If has already ran once then unsbcribe
								if (ranOnce) {
									storeSubscription.unsubscribe();
								}
							}
						}
					}
				}
			});

			ranOnce = true;
			// If has values on first run then unsbcribe
			if (resolved) {
				storeSubscription.unsubscribe();
			}
		});
	}

	//#region REDUX OPERATIONS
	public redux_getActivitiesSelector() {
		return this.store.select(activities)
	}

	public redux_loadArchievedActivity() {
		this.store.dispatch(loadArchievedActivity());
	}

	public redux_addOrUpdateActivity(listener: SignalRListener, activity: ActivityLog[]) {
		this.store.dispatch(addOrUpdateActivity({ listener, activity }));
	}

	public redux_resetArchivedActivity() {
		this.store.dispatch(resetArchivedActivity())
	}
	//#endregion
}
